<template>
  <div class="gift-claim-page">
    <div class="card">
      <img src="/source/png/nftwerks-logo.png" alt="NFTWERKS" class="brand" />

      <div v-if="loading" class="state">Loading your gift…</div>

      <!-- Invalid / not found -->
      <div v-else-if="loadError" class="state state--error" data-testid="gift-claim-error">
        <h1 class="title">This gift link isn’t valid</h1>
        <p class="desc">{{ loadError }}</p>
        <router-link to="/events" class="btn-pill">Browse events</router-link>
      </div>

      <!-- Already claimed -->
      <div v-else-if="preview.status === 'claimed' && !justClaimed" class="state">
        <h1 class="title">These tickets have already been claimed</h1>
        <p class="desc">If you claimed them, you can find them in My Tickets.</p>
        <router-link to="/tickets" class="btn-pill">Go to My Tickets</router-link>
      </div>

      <!-- Expired -->
      <div v-else-if="preview.status === 'expired'" class="state state--error">
        <h1 class="title">This gift link has expired</h1>
        <p class="desc">Please contact support for help.</p>
      </div>

      <!-- Not ready yet (payment still settling) -->
      <div v-else-if="preview.status === 'not_ready'" class="state">
        <h1 class="title">Your tickets aren’t ready yet</h1>
        <p class="desc">We’re still processing this gift. Please try again in a minute.</p>
        <button class="btn-pill" @click="reload">Refresh</button>
      </div>

      <!-- Claimed just now -->
      <div v-else-if="justClaimed" class="state" data-testid="gift-claim-done">
        <div class="tick">🎉</div>
        <h1 class="title">Tickets claimed!</h1>
        <p class="desc">
          {{ preview.ticketCount }} {{ preview.ticketCount === 1 ? 'ticket' : 'tickets' }} for
          <strong>{{ preview.eventTitle }}</strong> {{ walletStatusText }}.
        </p>
        <p v-if="walletStatus === 'pending'" class="desc desc--muted">
          This can take a minute — check My Tickets for the latest status.
        </p>
        <router-link to="/tickets" class="btn-pill">View My Tickets</router-link>
      </div>

      <!-- Claimable -->
      <div v-else class="state" data-testid="gift-claim-ready">
        <p class="from">{{ preview.senderName }} sent you tickets 🎁</p>
        <h1 class="title">{{ preview.eventTitle }}</h1>
        <p v-if="eventWhen" class="desc">{{ eventWhen }}<span v-if="preview.venue"> · {{ preview.venue }}</span></p>

        <div v-if="preview.message" class="message">
          “{{ preview.message }}”
          <span class="message__from">— {{ preview.senderName }}</span>
        </div>

        <ul class="tiers">
          <li v-for="(t, i) in preview.tiers" :key="i">{{ t.quantity }} × {{ t.name }}</li>
        </ul>

        <template v-if="isLoggedIn">
          <button
            v-if="!showWallet"
            class="wallet-toggle"
            @click="showWallet = true"
          >+ Add a crypto wallet (optional)</button>
          <div v-else class="wallet-field">
            <input
              v-model="walletAddress"
              placeholder="0x… wallet address"
              class="input"
              data-testid="claim-wallet"
              @input="claimError = ''"
            />
            <p class="hint">You can skip this and add a wallet later.</p>
          </div>

          <p v-if="claimError" class="err" data-testid="claim-error">{{ claimError }}</p>

          <button class="btn-pill btn-pill--primary" :disabled="claiming" @click="claim" data-testid="claim-btn">
            {{ claiming ? 'Claiming…' : 'Claim your tickets' }}
          </button>
        </template>

        <template v-else>
          <p class="desc">Sign in or create a free account to claim these tickets.</p>
          <button class="btn-pill btn-pill--primary" @click="showLogin = true" data-testid="claim-signin">
            Sign in to claim
          </button>
        </template>
      </div>
    </div>

    <login-modal
      v-if="showLogin"
      @close="showLogin = false"
      @success="onLoginSuccess"
    />
  </div>
</template>

<script>
import ticketingApi from '@/api/ticketing'
import LoginModal from '@/components/events/LoginModal.vue'
import { getCookie } from '@/assets/js/cookie'

export default {
  name: 'GiftClaim',
  components: { LoginModal },
  data() {
    return {
      token: this.$route.query.token || '',
      loading: true,
      loadError: '',
      preview: {},
      showLogin: false,
      showWallet: false,
      walletAddress: '',
      claiming: false,
      claimError: '',
      justClaimed: false,
      walletStatus: 'not_requested',
      // A data field, not a computed — getCookie() reads document.cookie, which
      // Vue can't track as a reactive dependency. As a computed this evaluated
      // once and never again, so logging in via the modal (LoginModal writes
      // the cookie directly, outside Vue's reactivity) never flipped it: the
      // page stayed on "Sign in to claim" until a manual reload. Set explicitly
      // in onLoginSuccess below, where login actually happens.
      isLoggedIn: !!getCookie('token'),
    }
  },
  computed: {
    walletStatusText() {
      return {
        pending: 'are on their way to your wallet',
        transferred: 'have been sent to your wallet',
        failed: 'have been added to your account',
        not_requested: 'have been added to your account',
      }[this.walletStatus] || 'have been added to your account'
    },
    eventWhen() {
      if (!this.preview.eventDate) return ''
      return new Date(this.preview.eventDate).toLocaleString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    },
  },
  created() {
    if (!this.token) {
      this.loadError = 'The link is missing its claim code.'
      this.loading = false
      return
    }
    this.loadPreview()
  },
  methods: {
    async loadPreview() {
      this.loading = true
      try {
        const res = await ticketingApi.getGiftClaim(this.token)
        this.preview = res.data || res
      } catch (err) {
        const status = err && err.response && err.response.status
        this.loadError = status === 404
          ? 'This gift link is not valid or has been used already.'
          : 'We couldn’t load this gift. Please try again shortly.'
      } finally {
        this.loading = false
      }
    },
    reload() {
      this.loadPreview()
    },
    onLoginSuccess() {
      this.showLogin = false
      this.isLoggedIn = true
      this.loadPreview()
    },
    async claim() {
      const wallet = this.walletAddress.trim()
      if (wallet && !/^0x[0-9a-fA-F]{40}$/.test(wallet)) {
        this.claimError = 'That doesn’t look like a valid wallet address (0x…).'
        return
      }
      this.claiming = true
      this.claimError = ''
      try {
        const res = await ticketingApi.claimGift({
          token: this.token,
          walletAddress: wallet || undefined,
        })
        const result = res.data || res
        this.walletStatus = (result && result.walletStatus) || 'not_requested'
        this.justClaimed = true
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.message) || err.message
        this.claimError = Array.isArray(msg) ? msg.join(', ') : (msg || 'Could not claim these tickets. Please try again.')
      } finally {
        this.claiming = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
$font: 'Inter', sans-serif;
$text: #0a0a0a;
$muted: rgba(10, 10, 10, 0.55);
$gradient: linear-gradient(135deg, #8b5cf6, #ec4899);

.gift-claim-page {
  font-family: $font;
  background: #f0f0f5;
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 64px 16px;
  color: $text;
}

.card {
  background: #fff;
  border-radius: 24px;
  padding: 40px 32px;
  width: 100%;
  max-width: 440px;
  text-align: center;
}

.brand { width: 150px; margin: 0 auto 24px; display: block; }

.state { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.state--error .title { color: #c0392b; }

.from { font-size: 14px; font-weight: 700; color: #8b5cf6; margin: 0; }
.title { font-size: 22px; font-weight: 800; margin: 0; line-height: 1.3; }
.desc { font-size: 14px; color: $muted; line-height: 1.6; margin: 0; }
.desc--muted { font-size: 12px; }
.tick { font-size: 44px; }

.message {
  background: #faf5ff;
  border: 1px solid #ece0ff;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  font-style: italic;
  color: #4b4b57;
  &__from { display: block; font-style: normal; color: $muted; margin-top: 6px; }
}

.tiers {
  list-style: none; padding: 0; margin: 4px 0;
  display: flex; flex-direction: column; gap: 6px;
  font-size: 15px; font-weight: 600;
}

.wallet-toggle {
  background: none; border: none; color: #8b5cf6;
  font-size: 13px; font-weight: 600; cursor: pointer; font-family: $font;
}
.wallet-field { width: 100%; display: flex; flex-direction: column; gap: 6px; }
.input {
  width: 100%; background: #f3f4f6; border: none; border-radius: 12px;
  padding: 14px 18px; font-size: 15px; font-family: $font; outline: none;
}
.hint { font-size: 12px; color: $muted; margin: 0; text-align: left; }
.err { font-size: 13px; color: #dc2626; margin: 0; }

.btn-pill {
  display: inline-block;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: #fff;
  border-radius: 44px;
  padding: 12px 26px;
  font-size: 14px;
  font-weight: 700;
  font-family: $font;
  color: $text;
  text-decoration: none;
  cursor: pointer;
  &--primary {
    border: none;
    background: $gradient;
    color: #fff;
    width: 100%;
  }
  &:disabled { opacity: 0.6; cursor: default; }
}
</style>
